import {
  and,
  arrayRemove,
  arrayUnion,
  collection,
  CollectionReference,
  deleteField,
  doc,
  DocumentData,
  documentId,
  FieldValue,
  getCountFromServer,
  getDoc,
  getDocs,
  limit,
  or,
  orderBy,
  query,
  Query,
  QueryCompositeFilterConstraint,
  QueryDocumentSnapshot,
  QueryFieldFilterConstraint,
  setDoc,
  startAfter,
  updateDoc,
  where,
} from '@firebase/firestore';
import {
  ChatDocument,
  ChatInput,
  ChatMessageDocument,
  ChatMessageInput,
  chatMessagesSchema,
  chatSchema,
  SupportState,
} from '@models/chat';
import type { OrderDocument } from '@models/order';
import {
  db,
  FirebaseCallable,
  functions,
  httpsCallable,
  storage,
} from '@util/firebase';
import { logError } from '@util/logError';
import type { ContactUsForm } from 'app/contact/components/ContactUs';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { findPhoneNumbersInText } from 'libphonenumber-js';
import type { ProductDocument } from 'models/product';
import type { SMSDocument } from 'models/sms';
import { UserDocument } from 'models/user';

export const messagesRef = collection(
  db,
  'messages'
) as CollectionReference<ChatDocument>;

export type ChatFilterType =
  | 'all'
  | 'unread'
  | 'flagged'
  | 'offers'
  | 'offers_pending'
  | 'offers_accepted'
  | 'offers_declined'
  | 'offers_expired'
  | 'buying'
  | 'selling'
  | 'support'
  | 'messages'
  | 'experts';

export type MainFilters =
  | 'all'
  | 'unread'
  | 'offers'
  | 'buy'
  | 'flagged'
  | 'sell'
  | 'support'
  | 'offers_all'
  | 'offers_pending'
  | 'offers_accepted'
  | 'offers_declined'
  | 'offers_expired';

export const MAIN_FILTERS = {
  all: 'All',
  unread: 'Unread',
  offers: 'Offers',
  buy: 'Buying',
  sell: 'Selling',
  support: 'Support',
  flagged: 'Flagged',
  offer_states: {
    offers_all: 'All',
    offers_pending: 'Pending',
    offers_accepted: 'Accepted',
    offers_declined: 'Declined',
    offers_expired: 'Expired',
  },
} as const;

export type OfferFilters =
  | 'all'
  | 'buy'
  | 'sell'
  | 'pending'
  | 'accepted'
  | 'declined'
  | 'expired';

export function getChatFilterConstraint({
  uid,
  mainFilter,
}: {
  uid: string;
  mainFilter: MainFilters;
}): QueryFieldFilterConstraint | undefined {
  if (mainFilter === 'unread') {
    return where('unread.' + uid, '==', true);
  }

  if (mainFilter === 'offers' || mainFilter.startsWith('offers_')) {
    return where('is_offer', '==', true);
  }

  if (mainFilter === 'buy') {
    return where('buyer_id', '==', uid);
  }

  if (mainFilter === 'sell') {
    return where('seller_id', '==', uid);
  }

  if (mainFilter === 'flagged') {
    return where('flagged', '==', true);
  }

  return undefined;
}

export function getChatsQuery({
  userDoc,
  mainFilter,
  lastDoc,
}: {
  userDoc: UserDocument | null | undefined;
  mainFilter: MainFilters;
  lastDoc?: QueryDocumentSnapshot<ChatDocument>;
}) {
  let q = query(messagesRef);
  const isSupport = userDoc?.roles?.support || userDoc?.roles?.admin;
  const shouldLoadAll =
    isSupport &&
    (mainFilter === 'all' ||
      mainFilter.startsWith('offers_') ||
      mainFilter === 'offers');

  if (!shouldLoadAll) {
    q = query(q, where('uids', 'array-contains', userDoc?.uid));
  }

  const filterConstraint = getChatFilterConstraint({
    uid: userDoc?.uid ?? '',
    mainFilter,
  });

  if (filterConstraint) {
    q = query(q, filterConstraint);
  }

  // adding order by constraints
  if (mainFilter === 'support') {
    q = query(q, orderBy('case_num', 'desc'));
  }

  if (
    mainFilter === 'offers' ||
    mainFilter.startsWith('offers_') ||
    mainFilter === 'buy' ||
    mainFilter === 'sell'
  ) {
    q = query(q, orderBy('last_time', 'desc'));
  }

  q = query(q, limit(mainFilter === 'unread' ? 50 : 20));

  if (lastDoc) q = query(q, startAfter(lastDoc));

  return q;
}

export async function getChatsByIds(
  ids: string[],
  userDoc: UserDocument | null | undefined
) {
  try {
    const isSupport = userDoc?.roles?.support || userDoc?.roles?.admin;
    const idsToQuery = ids.slice(0, 30);

    if (isSupport) {
      const q = query(messagesRef, where(documentId(), 'in', idsToQuery));
      const { docs } = await getDocs(q);

      return docs.map((d) => d.data());
    } else {
      const q = query(
        messagesRef,
        where(documentId(), 'in', idsToQuery),
        where('uids', 'array-contains', userDoc?.uid ?? '')
      );
      const { docs } = await getDocs(q);

      return docs.map((d) => d.data());
    }
  } catch (_e) {
    const results = await Promise.allSettled(
      ids.map((id) => getDoc(doc(messagesRef, id)))
    );
    const chats: ChatDocument[] = [];
    results.forEach((r) => {
      if (r.status === 'fulfilled') {
        const chat = r.value.data();
        if (chat) chats.push(chat);
      }
    });
    return chats;
  }
}

export function getChatsByUidQuery(
  userDoc: UserDocument,
  variant?: 'messages' | 'offers' | 'orderDetail' | 'experts'
) {
  const constraints = [];
  const isSupport = userDoc.roles?.support || userDoc.roles?.admin;

  if (!isSupport) {
    constraints.push(where('uids', 'array-contains', userDoc.uid));
  }

  if (variant === 'offers') {
    constraints.push(where('is_offer', '==', true));
  }

  constraints.push(orderBy('last_time', 'desc'), limit(50));
  return query(messagesRef, ...constraints);
}

export function getChatsBySearchQuery(
  searchQuery: string,
  userDoc: UserDocument | null | undefined
) {
  const searchQueryLower = searchQuery.toLowerCase();
  let q: Query<ChatDocument, DocumentData>;

  q = query(
    messagesRef,
    and(
      where('uids', 'array-contains', userDoc?.uid ?? ''),
      or(
        where('order_num', '==', Number(searchQueryLower.replaceAll('#', ''))),
        where('case_num', '==', Number(searchQueryLower.replaceAll('#', '')))
      )
    ),
    limit(50)
  );

  return q;
}

export function getChatById(chat_id: string) {
  return doc(messagesRef, chat_id);
}

export async function getChatDocumentById(chat_id: string) {
  const snapshot = await getDoc(getChatById(chat_id));
  const chat: ChatDocument | undefined = snapshot.data();
  if (!chat) return null;
  return chat;
}

export async function getChatByCaseNumber(case_num: number) {
  const q = query(messagesRef, where('case_num', '==', case_num), limit(1));
  const snap = await getDocs(q);
  const doc = snap.docs[0]?.data() as ChatDocument | undefined;
  return doc;
}

export async function getChatsForOrderDetails(order: OrderDocument) {
  const promises = [getChatsByOrderId(order.id)];
  if (order.product_ids && order.seller_arr)
    promises.push(
      getChatsByProductIds(order.product_ids, order.seller_arr, order.buyer_id)
    );
  const allChats = (await Promise.all(promises)).flat();
  const uniqueChatsMap = new Map<string, ChatDocument>();
  for (const chat of allChats) {
    if (!uniqueChatsMap.has(chat.id)) {
      uniqueChatsMap.set(chat.id, chat);
    }
  }
  const chats = Array.from(uniqueChatsMap.values()).sort(
    (a, b) => b.last_time - a.last_time
  );
  return chats;
}

export async function getChatsForSellerAdminControls(uid: string, l?: number) {
  try {
    const q = l
      ? query(
          messagesRef,
          where('uids', 'array-contains', uid),
          orderBy('last_time', 'desc'),
          limit(l)
        )
      : query(
          messagesRef,
          where('uids', 'array-contains', uid),
          orderBy('last_time', 'desc')
        );
    const docs = await getDocs(q);
    return docs.docs.map((doc) => doc.data());
  } catch (e) {
    logError(e as Error);
    return [];
  }
}
async function getChatsByProductIds(
  products: string[],
  seller_ids: string[],
  buyer_id: string
) {
  try {
    const q = query(
      messagesRef,
      where('product_id', 'in', products),
      where('buyer_id', '==', buyer_id)
    );
    const docs = await getDocs(q);
    return docs.docs
      .map((doc) => doc.data())
      .filter((chat) => seller_ids.includes(chat.seller_id));
  } catch (e) {
    logError(e as Error);
    return [];
  }
}

export async function getChatsByOrderId(order_id: string) {
  const q = query(
    messagesRef,
    where('order_id', '==', order_id),
    orderBy('last_time', 'desc')
  );
  const docs = await getDocs(q);
  return docs.docs.map((doc) => doc.data());
}

export async function getSMSByOrderId(order_id: string) {
  const smsRef = collection(db, 'sms') as CollectionReference<SMSDocument>;
  const q = query(smsRef, where('order_id', '==', order_id));
  const { docs } = await getDocs(q);
  const sms = docs.map((doc) => doc.data());
  return sms.sort((a, b) => b.updated - a.updated);
}

export async function getTotalAdminBuyerChats() {
  try {
    const q = query(
      messagesRef,
      where('seller_id', '==', process.env.NEXT_PUBLIC_SUPPORT_ID),
      where('uids', 'array-contains', process.env.NEXT_PUBLIC_SUPPORT_ID),
      where('last_time', '>=', 1688011200000), // launch date
      orderBy('last_time', 'desc')
    );
    const autoQ = query(
      messagesRef,
      where('seller_id', '==', process.env.NEXT_PUBLIC_SUPPORT_ID),
      where('uids', 'array-contains', process.env.NEXT_PUBLIC_SUPPORT_ID),
      where('last_time', '>=', 1688011200000), // launch date,
      where('is_auto', '==', true),
      orderBy('last_time', 'desc')
    );
    const [count, autoCount] = await Promise.all([
      getCountFromServer(q),
      getCountFromServer(autoQ),
    ]);
    return count.data().count - autoCount.data().count;
  } catch (e) {
    logError(e as Error);
    return 0;
  }
}

export async function getTotalAdminSellerChats() {
  try {
    const q = query(
      messagesRef,
      where('buyer_id', '==', process.env.NEXT_PUBLIC_SUPPORT_ID),
      where('uids', 'array-contains', process.env.NEXT_PUBLIC_SUPPORT_ID),
      where('last_time', '>=', 1688011200000), // launch date
      orderBy('last_time', 'desc')
    );
    const autoQ = query(
      messagesRef,
      where('buyer_id', '==', process.env.NEXT_PUBLIC_SUPPORT_ID),
      where('uids', 'array-contains', process.env.NEXT_PUBLIC_SUPPORT_ID),
      where('last_time', '>=', 1688011200000), // launch date,
      where('is_auto', '==', true),
      orderBy('last_time', 'desc')
    );
    const [count, autoCount] = await Promise.all([
      getCountFromServer(q),
      getCountFromServer(autoQ),
    ]);
    return count.data().count - autoCount.data().count;
  } catch (e) {
    logError(e as Error);
    return 0;
  }
}

export async function loadMoreUserChats({
  uid,
  after,
  filter,
}: {
  uid: string;
  after?: string | number;
  filter?: QueryFieldFilterConstraint;
}) {
  const baseConstraints = [
    where('uids', 'array-contains', uid),
    orderBy('last_time', 'desc'),
    ...(after ? [startAfter(after)] : []),
    limit(50),
  ];

  const constraints = filter ? [...baseConstraints, filter] : baseConstraints;

  const q = query(messagesRef, ...constraints);

  const docs = await getDocs(q);
  return docs.docs.map((doc) => doc.data());
}

export async function getTwentyChats(lastChatTime?: number) {
  if (lastChatTime) {
    return await getNextTwenty(lastChatTime);
  }
  const q = query(messagesRef, orderBy('last_time', 'desc'), limit(20));
  const docs = await getDocs(q);
  return docs.docs.map((doc) => doc.data());
}

export async function getNextTwenty(lastChatTime: number) {
  const q = query(
    messagesRef,
    orderBy('last_time', 'desc'),
    startAfter(lastChatTime),
    limit(20)
  );
  const docs = await getDocs(q);
  return docs.docs.map((doc) => doc.data());
}

export async function getTwentySellerChats(lastChatTime?: number) {
  if (lastChatTime) {
    return await getNextTwentySeller(lastChatTime);
  }
  const q = query(
    messagesRef,
    where('buyer_id', '==', process.env.NEXT_PUBLIC_SUPPORT_ID),
    where('uids', 'array-contains', process.env.NEXT_PUBLIC_SUPPORT_ID),
    where('last_time', '>=', 1688011200000), // launch date
    orderBy('last_time', 'desc'),
    limit(20)
  );
  const docs = await getDocs(q);
  return docs.docs.map((doc) => doc.data());
}

export async function getNextTwentySeller(lastChatTime: number) {
  const q = query(
    messagesRef,
    where('buyer_id', '==', process.env.NEXT_PUBLIC_SUPPORT_ID),
    where('uids', 'array-contains', process.env.NEXT_PUBLIC_SUPPORT_ID),
    orderBy('last_time', 'desc'),
    where('last_time', '>=', 1688011200000), // launch date
    startAfter(lastChatTime),
    limit(20)
  );
  const docs = await getDocs(q);
  return docs.docs.map((doc) => doc.data());
}

export async function getTwentyBuyerChats(lastChatTime?: number) {
  if (lastChatTime) {
    return await getNextTwentyBuyer(lastChatTime);
  }
  const q = query(
    messagesRef,
    where('seller_id', '==', process.env.NEXT_PUBLIC_SUPPORT_ID),
    where('uids', 'array-contains', process.env.NEXT_PUBLIC_SUPPORT_ID),
    where('last_time', '>=', 1688011200000), // launch date
    orderBy('last_time', 'desc'),
    limit(20)
  );
  const docs = await getDocs(q);
  return docs.docs.map((doc) => doc.data());
}

export async function getNextTwentyBuyer(lastChatTime: number) {
  const q = query(
    messagesRef,
    where('seller_id', '==', process.env.NEXT_PUBLIC_SUPPORT_ID),
    where('uids', 'array-contains', process.env.NEXT_PUBLIC_SUPPORT_ID),
    where('last_time', '>=', 1688011200000), // launch date
    orderBy('last_time', 'desc'),
    startAfter(lastChatTime),
    limit(20)
  );
  const docs = await getDocs(q);
  return docs.docs.map((doc) => doc.data());
}

export async function getTwentyFlaggedChats(lastChatTime?: number) {
  if (lastChatTime) {
    return await getNextTwentyFlagged(lastChatTime);
  }
  const q = query(
    messagesRef,
    where('flagged', '==', true),
    orderBy('last_time', 'desc'),
    limit(20)
  );
  const docs = await getDocs(q);
  return docs.docs.map((doc) => doc.data());
}

export async function getNextTwentyFlagged(lastChatTime: number) {
  const q = query(
    messagesRef,
    where('flagged', '==', true),
    orderBy('last_time', 'desc'),
    startAfter(lastChatTime),
    limit(20)
  );
  const docs = await getDocs(q);
  return docs.docs.map((doc) => doc.data());
}

export async function sendMessageToChat(
  message: ChatMessageDocument | ChatMessageDocument[],
  chat: ChatDocument,
  currUserId?: string,
  product?: ProductDocument | null
) {
  let messages = Array.isArray(message) ? message : [message]; // normalize to array
  const redactedMessages = messages.map((m) => {
    const redactedMessage =
      currUserId === process.env.NEXT_PUBLIC_SUPPORT_ID ||
      chat.case_num ||
      product?.category === 'Dirt Bikes' ||
      chat.order_id
        ? m.content
        : redactPhonesAndEmailsFromStr(m.content, chat);
    return { ...m, content: redactedMessage };
  });
  messages = redactedMessages;
  const newChat = { ...chat };
  if (!newChat.unread) {
    newChat.unread = {};
  }

  if (currUserId === process.env.NEXT_PUBLIC_SUPPORT_ID && chat.uids) {
    newChat.unread = {};
    newChat.unread[chat.buyer_id] = true;
    newChat.unread[chat.seller_id] = true;
    newChat.unread[process.env.NEXT_PUBLIC_SUPPORT_ID!] = false;
  } else if (currUserId) {
    const recipient = currUserId === chat.uids[0] ? chat.uids[1] : chat.uids[0];
    newChat.unread = { [recipient]: true, [currUserId]: false };
  }

  if (newChat.messages) {
    newChat.messages.push(...messages);
  } else {
    newChat.messages = messages;
  }
  const now = Date.now();
  messages.forEach((m, i) => {
    m.created_at = now + i;
  });
  newChat.last_time = now;

  const docRef = doc(messagesRef, chat.id);
  const res = chatSchema.safeParse(newChat);
  if (res.success) {
    await setDoc(docRef, res.data);
  } else {
    logError(res.error, `Zod Error Chat:${chat.id} ${res.error.toString()}`);
    await setDoc(docRef, newChat);
  }
  return newChat;
}

export async function createChat(chat: ChatInput): Promise<ChatDocument> {
  const docRef = doc(messagesRef);
  const now = Date.now();
  chat.id = docRef.id;
  chat.last_time = now;
  chat.created_at = now;
  if (
    process.env.NEXT_PUBLIC_SUPPORT_ID &&
    chat.uids?.includes(process.env.NEXT_PUBLIC_SUPPORT_ID) &&
    !chat.case_num &&
    !chat.is_auto
  ) {
    chat.case_num = await getNextCaseNumber();
  }
  const res = chatSchema.safeParse(chat);
  if (res.success) {
    await setDoc(docRef, res.data);
    return res.data;
  } else {
    logError(res.error, `Zod Error Chat:${chat.id} ${res.error.toString()}`);
    await setDoc(docRef, chat);
    return chat as ChatDocument;
  }
}

export async function getChat(chat_id: string) {
  const docRef = doc(messagesRef, chat_id);
  const docSnap = await getDoc(docRef);
  if (docSnap.exists()) {
    return docSnap.data() as ChatDocument;
  } else {
    return null;
  }
}

export async function getNextCaseNumber() {
  const docRef = doc(messagesRef, 'count');
  const docSnap = await getDoc(docRef);
  const data = docSnap.data() as any as { count: number };
  await updateDoc(docRef, { count: data.count + 1 } as any);
  return data.count + 1;
}

export async function markAsRead(chat_id: string, uid: string) {
  const docRef = doc(messagesRef, chat_id);
  const key = `unread.${uid}`;
  await updateDoc(docRef, { [key]: false });
  // also update any notifications for this chat
  const notificationRef = collection(db, 'notifications');
  const q = query(
    notificationRef,
    where('uid', '==', uid),
    where('web_url', '==', `/dashboard/messages/${chat_id}`)
  );
  const docs = await getDocs(q);
  const notifications = docs.docs.map((doc) => doc.data());
  await Promise.all(
    notifications.map((n) =>
      updateDoc(doc(notificationRef, n.id), { read: true })
    )
  );
}

export async function markAsUnread(chat_id: string, uid: string) {
  const docRef = doc(messagesRef, chat_id);
  const key = `unread.${uid}`;
  await updateDoc(docRef, { [key]: true });
}

export async function flagChat(chat_id: string, flagged: boolean) {
  const docRef = doc(messagesRef, chat_id);
  await updateDoc(docRef, { flagged });
}

export async function getFlaggedChats() {
  try {
    const q = query(
      messagesRef,
      where('flagged', '==', true),
      orderBy('last_time', 'desc')
    );
    const docs = await getDocs(q);
    return docs.docs.map((doc) => doc.data());
  } catch (e) {
    logError(e as Error);
    return [];
  }
}

export async function hideChat(chat_id: string, uid: string) {
  const docRef = doc(messagesRef, chat_id);
  await updateDoc(docRef, {
    uids: arrayRemove(uid),
    [`unread.${uid}`]: deleteField(),
  });
}

export async function hideAllChatsForUser(uid: string) {
  const chats = await getChatsForSellerAdminControls(uid);
  await Promise.all(
    chats.map((chat) =>
      Promise.all(
        (chat.uids || [])
          .filter((id) => id !== uid)
          .map((id) => hideChat(chat.id, id))
      )
    )
  );
}

export async function removeCaseNumber(chat_id: string) {
  const uid = process.env.NEXT_PUBLIC_SUPPORT_ID!;
  const docRef = doc(messagesRef, chat_id);
  await updateDoc(docRef, {
    case_num: deleteField(),
    [`unread.${uid}`]: deleteField(),
    uids: arrayRemove(uid),
  });
}

export async function resolveChat(chat_id: string, uid: string) {
  const docRef = doc(messagesRef, chat_id);
  const autoMessage: ChatMessageInput = {
    uid: process.env.NEXT_PUBLIC_SUPPORT_ID!,
    content:
      'This chat has been resolved. Please open a new support ticket if you have a new issue.',
    created_at: Date.now(),
    is_auto: true,
  };
  const autoMessageParsed = chatMessagesSchema.parse(autoMessage);
  await updateDoc(docRef, {
    support_state: SupportState.Resolved,
    [`unread.${uid}`]: deleteField(),
    uids: arrayRemove(uid),
    messages: arrayUnion(autoMessageParsed),
  });
}

export async function deleteMessage(
  chat_id: string,
  message: ChatMessageDocument
) {
  const docRef = doc(messagesRef, chat_id);
  await updateDoc(docRef, { messages: arrayRemove(message) });
}

export async function uploadChatImage(
  data: Blob,
  user_id: string,
  chat_id: string
) {
  const path = `${user_id}/messages/${chat_id}/${Date.now()}`;
  const imagesRef = ref(storage, path);
  const res = await uploadBytes(imagesRef, data);
  try {
    const download_url = await getDownloadURL(res.ref);
    return { download_url, path: res.metadata.fullPath };
  } catch (e) {
    if ((e as Error).message.includes('does not exist')) {
      const prefix = `https://storage.googleapis.com/${process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET}/`;
      const fullPath = `${prefix}${res.metadata.fullPath}_1920x2560`;
      return { download_url: fullPath, path: res.metadata.fullPath };
    }
    throw e;
  }
}

export async function contactUs(form: ContactUsForm) {
  const res = await httpsCallable<ContactUsForm, { data: 'success' }>(
    functions,
    FirebaseCallable.contactUs
  )(form);
  return res.data.data;
}

export async function subscribeToList(email: string, listId?: string) {
  const res = await httpsCallable<{ email: string; listId?: string }>(
    functions,
    FirebaseCallable.subscribeToList
  )({ email, listId });
  return res.data;
}

export async function isSubscribedToList({
  email,
  listId,
}: {
  email: string;
  listId: string;
}) {
  try {
    const res = await httpsCallable<{ email: string; listId: string }>(
      functions,
      FirebaseCallable.isSubscribed
    )({ email, listId });
    return res.data;
  } catch (e) {
    logError(e as Error);
    return false;
  }
}

// Optional chat argument to flag a chat
export function redactPhonesAndEmailsFromStr(str: string, chat?: ChatDocument) {
  const res = findPhoneNumbersInText(str, 'US');
  const hasForbiddenWord = new RegExp(
    /^.*(venmo|zelle|paypal|pay pal|cash app|cashapp|instagram|facebook|imessage|ebay|email|whatsapp).*$/gi
  ).test(str.toLowerCase().replaceAll('.', ''));

  if (
    (res.length || hasForbiddenWord) &&
    !chat?.uids.includes('FjBH0nwiHgGV4Y0EXDQS') &&
    !chat?.uids.includes('134FT9kZZHYmJj3h9OBBwV7siq02') // ryan's account
  ) {
    if (chat) {
      const messsage = {
        is_auto: true,
        uid: process.env.NEXT_PUBLIC_SUPPORT_ID,
        content:
          'Please note buying or selling outside of MX Locker is a violation of our policies and will result in the ban of your account.',
        created_at: Date.now(),
      } as ChatMessageDocument;
      setTimeout(() => {
        sendMessageToChat(messsage, chat);
      }, 1000);
      flagChat(chat.id!, true);
    }
    res.forEach((r) => {
      const sub = str.substring(r.startsAt, r.endsAt);
      const redacted = new Array(sub.length).fill('█').join('');
      str = str!.replace(sub, redacted);
    });
  }
  const emailRegex = /\w+@\w+\.\w{2,3}/g;
  str = str.replace(emailRegex, '████████');
  return str;
}

export async function getUnreadMessagesCount(uid?: string) {
  if (!uid) return 0;

  const q = query(messagesRef, where(`unread.${uid}`, '==', true));

  const docs = await getCountFromServer(q);
  return docs.data().count ?? 0;
}

export async function getSupportTicketsBetween(start: Date, end: Date) {
  try {
    const q = query(
      messagesRef,
      where('created_at', '>=', start.getTime()),
      where('created_at', '<=', end.getTime())
    );
    const docs = await getDocs(q);
    const chats = docs.docs.map((doc) => doc.data());
    return chats.filter((chat) => chat.case_num);
  } catch (e) {
    logError(e as Error);
    return [];
  }
}

export async function updateChat(
  chat_id: string,
  data: Partial<ChatDocument> | Partial<Record<keyof ChatDocument, FieldValue>>
) {
  const docRef = doc(messagesRef, chat_id);
  await updateDoc(docRef, data);
}

export async function getChats(
  args: Pick<Partial<ChatDocument>, 'id' | 'buyer_id' | 'seller_id'>
) {
  const clauses = [];
  if (args.id) clauses.push(where('id', '==', args.id));
  if (args.buyer_id) clauses.push(where('buyer_id', '==', args.buyer_id));
  if (args.seller_id) clauses.push(where('seller_id', '==', args.seller_id));
  const q = query(messagesRef, ...clauses);
  const snap = await getDocs(q);
  return snap.docs.map((doc) => doc.data() as ChatDocument);
}

export async function getChatForContact({
  fields,
  userDoc,
}: {
  userDoc: UserDocument;
  fields: {
    product_id?: string;
    order_id?: string;
    recipient_uid: string;
  };
}) {
  const baseConstraints = [where('uids', 'array-contains', userDoc?.uid)];

  if (fields.product_id) {
    baseConstraints.push(where('product_id', '==', fields.product_id));
  } else {
    baseConstraints.push(where('product_id', '==', ''));
  }

  if (fields.order_id) {
    baseConstraints.push(where('order_id', '==', fields.order_id));
  } else {
    baseConstraints.push(where('order_id', '==', ''));
  }

  let userFilter: QueryFieldFilterConstraint | QueryCompositeFilterConstraint;

  if (fields.recipient_uid) {
    userFilter = or(
      and(
        where('seller_id', '==', userDoc?.uid),
        where('buyer_id', '==', fields.recipient_uid)
      ),
      and(
        where('buyer_id', '==', userDoc?.uid),
        where('seller_id', '==', fields.recipient_uid)
      )
    );
  } else {
    userFilter = or(
      where('seller_id', '==', userDoc?.uid),
      where('buyer_id', '==', userDoc?.uid)
    );
  }

  const q = query(messagesRef, and(...baseConstraints, userFilter));
  const snap = await getDocs(q);
  const results = snap.docs.map((doc) => doc.data() as ChatDocument);

  return results;
}
